import { useTranslation } from 'react-i18next';
import {
  LANGUAGE_LABELS,
  LANGUAGE_NAMES,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  normalizeLanguage,
} from '../../i18n/config';

type LanguageSwitcherProps = {
  scale?: number;
  fullWidth?: boolean;
};

export default function LanguageSwitcher({ scale = 1, fullWidth = false }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const activeLanguage = normalizeLanguage(i18n.resolvedLanguage || i18n.language);

  const handleLanguageChange = (language: string) => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizeLanguage(language));
    void i18n.changeLanguage(language);
  };

  return (
    <div
      className={fullWidth ? 'w-full' : ''}
      style={{ transform: `scale(${scale})`, transformOrigin: 'center right' }}
    >
      <label className="sr-only" htmlFor="language-switcher-select">
        {t('nav.languageLabel')}
      </label>

      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-3.5 z-10 flex items-center text-white/52">
          <i className="ri-global-line text-[1rem]" />
        </span>

        <select
          id="language-switcher-select"
          value={activeLanguage}
          onChange={(event) => handleLanguageChange(event.target.value)}
          className={[
            'h-11 appearance-none rounded-[18px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.06),rgba(255,255,255,0.02))] pl-10 pr-20 text-sm font-medium text-white outline-none backdrop-blur-xl transition-all duration-300 hover:border-white/14 hover:bg-[linear-gradient(180deg,rgba(255,255,255,0.075),rgba(255,255,255,0.03))] focus:border-white/16 focus:bg-[linear-gradient(180deg,rgba(255,255,255,0.085),rgba(255,255,255,0.035))]',
            'shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_12px_30px_rgba(0,0,0,0.2)]',
            fullWidth ? 'w-full min-w-0' : 'min-w-[158px]',
          ].join(' ')}
          aria-label={t('nav.languageLabel')}
        >
          {SUPPORTED_LANGUAGES.map((language) => (
            <option key={language} value={language} className="bg-[#101010] text-white">
              {LANGUAGE_NAMES[language]}
            </option>
          ))}
        </select>

        <span className="pointer-events-none absolute inset-y-0 right-11 z-10 flex items-center text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-white/48">
          {LANGUAGE_LABELS[activeLanguage]}
        </span>

        <span className="pointer-events-none absolute inset-y-0 right-3.5 z-10 flex items-center text-white/52">
          <i className="ri-arrow-down-s-line text-base" />
        </span>
      </div>
    </div>
  );
}
